import { useState, useRef, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
    Box,
    Flex,
    HStack,
    VStack,
    ModalOverlay,
    ModalCloseButton,
    Icon,
    Slider,
    SliderTrack,
    SliderFilledTrack,
    SliderThumb,
} from '@chakra-ui/react';
import {
    LuZoomIn as ZoomIn,
    LuZoomOut as ZoomOut,
    LuCheck as Check,
} from 'react-icons/lu';
import {
    Button,
    Modal,
    ModalBody,
    ModalContent,
    ModalHeader,
    ModalFooter,
    Text,
} from '@/components/v4';

import { Handle } from 'pages/organisations/partners/edit/Handle';

const ImageCropperModal = ({
    isOpen,
    onClose,
    imageSrc,
    aspectRatio: initialAspectRatio,
    maxWidth,
    maxHeight,
    onApply,
    target,
}) => {
    const [zoom, setZoom] = useState(1);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [lastPos, setLastPos] = useState({ x: 0, y: 0 });
    const [baseScale, setBaseScale] = useState(1);
    const [cropBox, setCropBox] = useState({ width: 400, height: 200 });
    const [isResizing, setIsResizing] = useState(null);
    const containerRef = useRef(null);
    const imgRef = useRef(null);
    const { darkMode } = useSelector((state) => state.settings);

    useEffect(() => {
        if (isOpen) {
            setZoom(1);
            setOffset({ x: 0, y: 0 });
            setBaseScale(0);
            const w = 400;
            const h = Math.round(400 / initialAspectRatio);
            setCropBox({ width: w, height: h });

            // If image is already loaded (from cache), trigger load handler manually
            if (imgRef.current && imgRef.current.complete) {
                handleImageLoad({ target: imgRef.current });
            }
        }
    }, [isOpen, initialAspectRatio, imageSrc]);

    const handleImageLoad = (e) => {
        const img = e.target || e;
        const container = containerRef.current;
        if (!container) return;

        const updateScale = () => {
            const cW = container.offsetWidth;
            const cH = container.offsetHeight;

            // If container has no size yet (modal animating), retry
            if (cW === 0 || cH === 0) {
                requestAnimationFrame(updateScale);
                return;
            }

            const width = img.naturalWidth || img.clientWidth || 1;
            const height = img.naturalHeight || img.clientHeight || 1;
            const scale = Math.min(cW / width, cH / height);
            setBaseScale(scale);
        };
        updateScale();
    };

    const handleMouseDown = (e, type) => {
        e.stopPropagation();
        if (type === 'pan') {
            setIsDragging(true);
        } else {
            setIsResizing(type);
        }
        setLastPos({ x: e.clientX, y: e.clientY });
    };

    const handleMouseMove = useCallback(
        (e) => {
            if (!isDragging && !isResizing) return;
            const dx = e.clientX - lastPos.x;
            const dy = e.clientY - lastPos.y;
            if (isDragging) {
                setOffset((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
            } else if (isResizing) {
                setCropBox((prev) => {
                    let newW = prev.width;
                    let newH = prev.height;
                    if (isResizing.includes('e')) newW += dx * 2;
                    if (isResizing.includes('w')) newW -= dx * 2;
                    if (isResizing.includes('s')) newH += dy * 2;
                    if (isResizing.includes('n')) newH -= dy * 2;
                    newW = Math.max(40, Math.min(400, newW));
                    newH = Math.max(40, Math.min(400, newH));
                    return { width: newW, height: newH };
                });
            }
            setLastPos({ x: e.clientX, y: e.clientY });
        },
        [isDragging, isResizing, lastPos],
    );

    const handleMouseUp = () => {
        setIsDragging(false);
        setIsResizing(null);
    };

    const handleWheel = (e) => {
        const zoomStep = 0.05;
        const delta = e.deltaY < 0 ? zoomStep : -zoomStep;
        setZoom((prev) => Math.max(0.1, Math.min(5, prev + delta)));
    };

    useEffect(() => {
        if (isDragging || isResizing) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        } else {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        }
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, isResizing, handleMouseMove]);

    const getCroppedDimensions = () => {
        if (!imgRef.current || !containerRef.current) return { w: 0, h: 0 };
        const totalScale = zoom * baseScale;
        if (!totalScale || totalScale === Infinity) return { w: 0, h: 0 };
        const naturalW = Math.round(cropBox.width / totalScale);
        const naturalH = Math.round(cropBox.height / totalScale);
        return { w: naturalW, h: naturalH };
    };

    const calculateResizedDimensions = (
        srcWidth,
        srcHeight,
        maxWidth,
        maxHeight,
    ) => {
        const ratio = Math.min(maxWidth / srcWidth, maxHeight / srcHeight, 1);
        return {
            width: Math.round(srcWidth * ratio),
            height: Math.round(srcHeight * ratio),
        };
    };

    const applyCrop = () => {
        const { w: sourceW, h: sourceH } = getCroppedDimensions();
        if (sourceW <= 0 || sourceH <= 0) {
            console.error('Invalid crop dimensions');
            return;
        }
        const totalScale = zoom * baseScale;
        const img = imgRef.current;
        const target = calculateResizedDimensions(
            sourceW,
            sourceH,
            maxWidth,
            maxHeight,
        );
        const canvas = document.createElement('canvas');
        canvas.width = target.width;
        canvas.height = target.height;
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        const sourceCenterX = img.naturalWidth / 2 - offset.x / totalScale;
        const sourceCenterY = img.naturalHeight / 2 - offset.y / totalScale;
        const sx = sourceCenterX - sourceW / 2;
        const sy = sourceCenterY - sourceH / 2;
        ctx.drawImage(
            img,
            sx,
            sy,
            sourceW,
            sourceH,
            0,
            0,
            canvas.width,
            canvas.height,
        );
        onApply(canvas.toDataURL('image/png'), canvas.width, canvas.height);
    };

    const currentDims = getCroppedDimensions();

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="2xl" isCentered>
            <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
            <ModalContent
                data-testid="image-cropper-modal"
                borderRadius="2xl"
                overflow="hidden">
                <ModalHeader
                    borderBottomWidth="1px"
                    py={6}
                    borderColor={darkMode ? '#858586' : 'gray.200'}>
                    <VStack align="flex-start" spacing={1}>
                        <Text fontSize="lg" fontWeight="bold">
                            Resize and Crop Asset
                        </Text>
                        <Text
                            fontSize="xs"
                            color="gray.500"
                            fontWeight="medium">
                            Drag blue edges to resize frame • Drag center to pan
                            image
                        </Text>
                    </VStack>
                    <ModalCloseButton top={6} right={6} />
                </ModalHeader>
                <ModalBody p={8}>
                    <VStack spacing={8}>
                        <Box
                            ref={containerRef}
                            position="relative"
                            bg={target === 'dark' ? '#262626' : 'white'}
                            borderRadius="md"
                            shadow="inner"
                            overflow="hidden"
                            w="400px"
                            h="400px"
                            borderWidth="1px"
                            onWheel={handleWheel}>
                            <Box
                                position="absolute"
                                inset="0"
                                cursor="move"
                                zIndex="10"
                                onMouseDown={(e) => handleMouseDown(e, 'pan')}>
                                <img
                                    ref={imgRef}
                                    src={imageSrc}
                                    alt="To crop"
                                    onLoad={handleImageLoad}
                                    style={{
                                        position: 'absolute',
                                        pointerEvents: 'none',
                                        transformOrigin: 'center',
                                        width: 'auto',
                                        height: 'auto',
                                        transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px)) scale(${zoom * baseScale})`,
                                        top: '50%',
                                        left: '50%',
                                        maxWidth: 'none',
                                    }}
                                />
                            </Box>
                            <Box
                                position="absolute"
                                top="50%"
                                left="50%"
                                transform="translate(-50%, -50%)"
                                pointerEvents="none"
                                zIndex="20"
                                border="2px solid"
                                borderColor="messenger.600"
                                shadow="0 0 0 9999px rgba(241, 245, 249, 0.85)"
                                w={`${cropBox.width}px`}
                                h={`${cropBox.height}px`}>
                                <Handle pos="n" onMouseDown={handleMouseDown} />{' '}
                                <Handle pos="s" onMouseDown={handleMouseDown} />{' '}
                                <Handle pos="e" onMouseDown={handleMouseDown} />{' '}
                                <Handle pos="w" onMouseDown={handleMouseDown} />
                                <Handle
                                    pos="ne"
                                    onMouseDown={handleMouseDown}
                                />{' '}
                                <Handle
                                    pos="nw"
                                    onMouseDown={handleMouseDown}
                                />{' '}
                                <Handle
                                    pos="se"
                                    onMouseDown={handleMouseDown}
                                />{' '}
                                <Handle
                                    pos="sw"
                                    onMouseDown={handleMouseDown}
                                />
                                <Box
                                    position="absolute"
                                    top="33.33%"
                                    left="0"
                                    w="full"
                                    h="px"
                                    bg="messenger.600"
                                    opacity="0.2"
                                />
                                <Box
                                    position="absolute"
                                    top="66.66%"
                                    left="0"
                                    w="full"
                                    h="px"
                                    bg="messenger.600"
                                    opacity="0.2"
                                />
                                <Box
                                    position="absolute"
                                    left="33.33%"
                                    top="0"
                                    w="px"
                                    h="full"
                                    bg="messenger.600"
                                    opacity="0.2"
                                />
                                <Box
                                    position="absolute"
                                    left="66.66%"
                                    top="0"
                                    w="px"
                                    h="full"
                                    bg="messenger.600"
                                    opacity="0.2"
                                />
                            </Box>
                        </Box>

                        <VStack w="full" maxW="400px" spacing={6}>
                            <HStack w="full" spacing={4}>
                                <Icon as={ZoomOut} color="gray.400" />
                                <Slider
                                    aria-label="zoom-slider"
                                    min={0.1}
                                    max={5}
                                    step={0.01}
                                    value={zoom}
                                    onChange={setZoom}>
                                    <SliderTrack bg="gray.200" h="1.5">
                                        <SliderFilledTrack bg="messenger.600" />
                                    </SliderTrack>
                                    <SliderThumb boxSize={4} />
                                </Slider>
                                <Icon as={ZoomIn} color="gray.400" />
                            </HStack>
                            <Flex
                                w="full"
                                justify="space-between"
                                align="center"
                                px={2}>
                                <VStack align="flex-start" spacing={0}>
                                    <Text
                                        fontSize="10px"
                                        color={
                                            darkMode ? 'gray.200' : 'gray.400'
                                        }
                                        fontWeight="bold"
                                        letterSpacing="widest"
                                        textTransform="uppercase">
                                        Crop resolution
                                    </Text>
                                    <Text
                                        fontSize="xs"
                                        fontWeight="bold"
                                        color="gray.600">
                                        Visible pixels: {currentDims.w} x{' '}
                                        {currentDims.h}px
                                    </Text>
                                </VStack>
                                <Box
                                    bg={
                                        darkMode
                                            ? 'messenger.500'
                                            : 'messenger.50'
                                    }
                                    px={2}
                                    py={1}
                                    borderRadius="md">
                                    <Text
                                        fontSize="10px"
                                        fontWeight="bold"
                                        color="gray.600">
                                        Zoom: {Math.round(zoom * 100)}%
                                    </Text>
                                </Box>
                            </Flex>
                        </VStack>
                    </VStack>
                </ModalBody>
                <ModalFooter
                    borderTopWidth="1px"
                    borderColor={darkMode ? '#858586' : 'gray.200'}>
                    <Button
                        className="secondary h-10 min-w-[5rem] font-semibold"
                        variant="outline"
                        background="white"
                        sx={{ width: 142 }}
                        mr={3}
                        onClick={onClose}
                        fontWeight="bold">
                        Cancel
                    </Button>
                    <Button
                        leftIcon={<Icon as={Check} />}
                        onClick={applyCrop}
                        fontWeight="bold"
                        shadow="lg">
                        Apply Crop
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default ImageCropperModal;
